(() => {
  const KEY = 'um_wishlist';

  const readWishlist = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
    catch (_) { return []; }
  };

  const writeWishlist = (value) => {
    try { localStorage.setItem(KEY, JSON.stringify(value)); }
    catch (_) {}
  };

  const syncWishlistButtons = (scope = document) => {
    const list = readWishlist().map(String);
    scope.querySelectorAll('[data-utes-wishlist]').forEach((button) => {
      const id = String(button.dataset.productId || '');
      const active = id && list.includes(id);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? 'Von der Wunschliste entfernen' : 'Zur Wunschliste hinzufügen');
    });
  };

  const ensureVideoChip = (scope = document) => {
    scope.querySelectorAll('.product-information__media').forEach((mediaColumn) => {
      if (mediaColumn.querySelector('.utes-pdp__video-chip')) return;
      const marker = mediaColumn.querySelector('[data-utes-first-video-index]');
      const slideshowContainer = mediaColumn.querySelector('slideshow-container');
      if (!marker || !slideshowContainer) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'utes-pdp__video-chip';
      button.dataset.utesGalleryIndex = marker.dataset.utesFirstVideoIndex;
      button.innerHTML = '<span aria-hidden="true">▶</span><span>Video ansehen</span>';
      slideshowContainer.appendChild(button);
    });
  };

  // Shrinks the payment-icon row so its total rendered width matches the
  // "In den Warenkorb" button, whatever the shop's enabled payment count.
  // The sticky add-to-cart bar at the bottom of the page renders its own
  // element with the same .add-to-cart-button class, so a bare
  // document-wide query can grab that one instead of the buybox's own
  // button - always resolve the button that lives in the same buybox.
  const updatePaymentIconWidth = (scope = document) => {
    scope.querySelectorAll('.utes-pdp__payments').forEach((list) => {
      const items = list.children.length;
      if (!items) return;
      const productDetails = list.closest('.product-details');
      const button = (productDetails && productDetails.querySelector('.add-to-cart-button'))
        || document.querySelector('.product-details .add-to-cart-button');
      const targetWidth = button ? button.getBoundingClientRect().width : 0;
      if (!targetWidth) return;

      const gapPx = parseFloat(getComputedStyle(list).columnGap) || 0;
      const totalGap = gapPx * Math.max(items - 1, 0);
      const iconWidth = Math.max(18, (targetWidth - totalGap) / items);
      list.style.setProperty('--utes-payment-icon-width', `${Math.round(iconWidth)}px`);
    });
  };

  let paymentWidthRaf = null;
  const schedulePaymentIconWidthUpdate = (scope = document) => {
    if (paymentWidthRaf) cancelAnimationFrame(paymentWidthRaf);
    paymentWidthRaf = requestAnimationFrame(() => updatePaymentIconWidth(scope));
  };

  // A single boot-time call can land before the add-to-cart button has
  // actually been laid out (section stylesheet not yet applied, fonts
  // still swapping in). Relying on window's "load" event alone isn't
  // safe either: this script can execute after "load" already fired, in
  // which case that listener would never fire again. A ResizeObserver on
  // the button itself self-heals from both: it fires again automatically
  // the moment its real size becomes available, and again on every later
  // change (e.g. viewport resize reflowing the buybox column).
  const paymentWidthResizeObserver = ('ResizeObserver' in window)
    ? new ResizeObserver(() => schedulePaymentIconWidthUpdate(document))
    : null;

  const observePaymentButton = () => {
    if (!paymentWidthResizeObserver) return;
    const atcButton = document.querySelector('.product-details .add-to-cart-button');
    if (atcButton) paymentWidthResizeObserver.observe(atcButton);
  };

  const boot = (scope = document) => {
    syncWishlistButtons(scope);
    ensureVideoChip(scope);
    schedulePaymentIconWidthUpdate(scope);
    observePaymentButton();
  };

  document.addEventListener('click', (event) => {
    const wishlist = event.target.closest('[data-utes-wishlist]');
    if (wishlist) {
      event.preventDefault();
      const id = String(wishlist.dataset.productId || '');
      if (!id) return;
      const list = readWishlist().map(String);
      const index = list.indexOf(id);
      if (index === -1) list.push(id); else list.splice(index, 1);
      writeWishlist(list);
      syncWishlistButtons(document);
      return;
    }

    const mediaTrigger = event.target.closest('[data-utes-gallery-index]');
    if (mediaTrigger) {
      event.preventDefault();
      const mediaColumn = mediaTrigger.closest('.product-information__media');
      if (!mediaColumn) return;
      const index = Number(mediaTrigger.dataset.utesGalleryIndex || 0);
      const thumbnails = mediaColumn.querySelectorAll('.slideshow-controls__thumbnail');
      const target = thumbnails[index];
      if (target) {
        target.click();
        mediaColumn.querySelector('slideshow-container')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      return;
    }

    const editorialPlay = event.target.closest('[data-utes-editorial-video-play]');
    if (editorialPlay) {
      event.preventDefault();
      const section = editorialPlay.closest('[data-utes-editorial]');
      const poster = section?.querySelector('[data-utes-editorial-video] .deferred-media__poster-button');
      if (poster) {
        poster.click();
        section.querySelector('[data-utes-editorial-video]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });

  window.addEventListener('resize', () => schedulePaymentIconWidthUpdate(document));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  // Horizon replaces product fragments when variants change. Re-apply small
  // progressive enhancements without taking over the native variant logic.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('.product-information, .product-details, .product-information__media') ||
            node.querySelector?.('.product-information, .product-details, .product-information__media')) {
          boot(node.closest?.('.product-information') || node);
          return;
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
